import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IActivityFeed, IAgentChat } from 'src/app/models/ActivityFeed';
import { BookingOrderBy, IBooking, IBookingListFilter } from 'src/app/models/BookingList';
import { IPagination } from 'src/app/models/Pagination';
import { getAgentFilterValue } from 'src/app/utils/helperFunctions';

export interface ICustomFilter {
    bookingListFilter: IBookingListFilter;
    bookingType: 'ABC' | 'CCD' | 'ALL';
    scheduledDate: Date | null;
    selectedJobType: ISelectedJobType;
    selectedJobStatus: ISelectedJobStatus;
    jobType: string[];
    showDelay: boolean;
    showCancel: boolean;
    operatingAirlineCode: string;
    operatingFlightNumber: number;
    operatingFlightDateTime: string;
    selectedBookingAgent: string;
    planType: IPlanType;
}

interface BookingState {
    bookings: IBooking[];
    user: any;
    filterByAirport: string;
    filterByAirline: string;
    commonFilter: string;
    hasfilterApplied: boolean;
    hasBookingFiltersApplied: boolean;
    customFilter: ICustomFilter;
    paginationResponse: IPagination;
    bookingsCount: number;
    userStatus: any;
    bookingDetails: IBooking;
    activityFeed: IActivityFeed[] | null;
    externalAgentChat: IAgentChat[] | null;
    orderBy: BookingOrderBy;
}

export type ISelectedJobType =
    | 'ALL'
    | 'Collections_and_Injections'
    | 'Acceptance'
    | 'Inject'
    | 'Collection_and_Delivery'
    | 'Collection'
    | 'Delivery'
    | 'Same_Day_Collections_and_Injections'
    | 'Same_Day_Collection_and_Delivery';

export type ISelectedJobStatus = 'ALL' | 'ACCEPTED_COLLECTED' | 'NOT_ACCEPTED_NOT_COLLECTED' | 'INJECTED_DELIVERED';
export type ISelectedAgent = 'ALL' | 'DiamondAirBookings' | 'HubDropBookings';
export type IPlanType = 'ALL' | 'Fixed' | 'SemiFlex' | 'FullyFlex'

const initialState: BookingState = {
    bookings: [],
    user: null,
    filterByAirport: 'ALL',
    filterByAirline: 'ALL',
    commonFilter: '',
    hasfilterApplied: false,
    hasBookingFiltersApplied: false,
    customFilter: {
        bookingListFilter: '',
        bookingType: 'ALL',
        scheduledDate: new Date(),
        selectedJobType: 'ALL',
        selectedJobStatus: 'ALL',
        jobType: [],
        showDelay: true,
        showCancel: true,
        operatingAirlineCode: '',
        operatingFlightNumber: 0,
        operatingFlightDateTime: '',
        selectedBookingAgent: 'ALL',
        planType: 'ALL',
    },
    paginationResponse: {
        pageNumber: 1,
        pageSize: 20,
    },
    bookingsCount: 0,
    userStatus: null,
    bookingDetails: null,
    activityFeed: null,
    externalAgentChat: null,
    orderBy: 'CollectionDateTimeAscending',
};

export const bookingSlice = createSlice({
    name: 'booking',
    initialState,
    reducers: {
        setCommonFilter: (state, action: PayloadAction<string>) => {
            state.commonFilter = action.payload;
        },
        setScheduledDate: (state, action: PayloadAction<Date | null>) => {
            state.customFilter.scheduledDate = action.payload;
            state.commonFilter = '';
        },
        setBookingListFilter: (state, action: PayloadAction<IBookingListFilter>) => {
            state.customFilter.bookingListFilter = action.payload;
            state.commonFilter = '';
            // state.hasBookingFiltersApplied = true;
            state.hasfilterApplied = true;
        },
        setBookingAgent: (state, action: PayloadAction<ISelectedAgent>) => { // state.customFilter.jobType = ['Acceptance'];
            state.customFilter.selectedBookingAgent = action.payload;

            state.customFilter.selectedJobType = 'ALL';
            state.customFilter.jobType = [];
            state.customFilter.selectedJobStatus = 'ALL';
            // if (action.payload === 'HubDropBookings' || action.payload === 'DiamondAirBookings') {
            //     state.customFilter.selectedJobType = 'Acceptance';
            //     state.customFilter.jobType = ['Acceptance'];
            //     state.customFilter.selectedJobStatus = 'ACCEPTED_COLLECTED';
            // }

            state.hasfilterApplied = true;
        },

        setDefaultBookingAgent: (state, action: PayloadAction<ISelectedAgent>) => {
            // state.customFilter.jobType = ['Acceptance'];
            state.customFilter.selectedBookingAgent = action.payload;

            state.customFilter.selectedJobType = 'ALL';
            state.customFilter.jobType = [];
            state.customFilter.selectedJobStatus = 'ALL';
            // if (action.payload === 'HubDropBookings' || action.payload === 'DiamondAirBookings') {
            //     state.customFilter.selectedJobType = 'Acceptance';
            //     state.customFilter.jobType = ['Acceptance'];
            //     state.customFilter.selectedJobStatus = 'ACCEPTED_COLLECTED';
            // }
        },

        setHubdropBookingFilter: (state, action: PayloadAction<ISelectedAgent>) => {
            state.customFilter.selectedBookingAgent = action.payload;
            state.customFilter.selectedJobType = 'ALL';
            state.customFilter.jobType = [];
            if (action.payload === 'HubDropBookings') {
                state.customFilter.selectedJobType = 'Acceptance';
                state.customFilter.jobType = ['Acceptance'];
                state.customFilter.selectedJobStatus = 'NOT_ACCEPTED_NOT_COLLECTED';
            }
        },

        applyBookingFilter: (state) => {
            state.hasfilterApplied = true;
        },
        applyJobFilter: (state) => {
            state.hasBookingFiltersApplied = true;
        },
        clearBookingFilters: (state, action) => {
            const { isHubDropUser, isDiamondAirUser } = action.payload;

            state.hasBookingFiltersApplied = false;

            state.customFilter.bookingListFilter = '';
            state.customFilter.bookingType = 'ALL';
            state.filterByAirport = 'ALL';
            state.filterByAirline = 'ALL';
            state.customFilter.scheduledDate = new Date();
            state.customFilter.selectedJobType = 'ALL';
            state.customFilter.selectedJobStatus = 'ALL';
            state.customFilter.jobType = [];

            state.customFilter.selectedBookingAgent = getAgentFilterValue(action.payload);

            if (isHubDropUser || isDiamondAirUser) {
                state.customFilter.selectedJobType = 'Acceptance';
                state.customFilter.jobType = ['Acceptance'];
                state.customFilter.selectedJobStatus = 'ACCEPTED_COLLECTED';
            }
        },
        clearAllFilter: (state, action) => {
            const { isHubDropUser, isDiamondAirUser } = action.payload;

            state.hasfilterApplied = false;
            state.hasBookingFiltersApplied = false;

            state.commonFilter = '';
            state.customFilter.bookingListFilter = '';
            state.filterByAirport = 'ALL';
            state.filterByAirline = 'ALL';
            state.customFilter.bookingType = 'ALL'; // used in bookingList page filters
            state.customFilter.scheduledDate = new Date();
            state.customFilter.selectedJobType = 'ALL'; // used in booking custom sidebar filters
            state.customFilter.selectedJobStatus = 'ALL';
            state.customFilter.jobType = [];

            state.customFilter.selectedBookingAgent = getAgentFilterValue(action.payload);

            if (isHubDropUser || isDiamondAirUser) {
                state.customFilter.selectedJobType = 'Acceptance';
                state.customFilter.jobType = ['Acceptance'];
                state.customFilter.selectedJobStatus = 'ACCEPTED_COLLECTED';
            }
        },
        setBookingtype: (state, action: PayloadAction<ISelectedJobType>) => {
            state.customFilter.selectedJobType = action.payload;
            // state.hasBookingFiltersApplied = true;
            state.commonFilter = '';
            state.hasfilterApplied = true;
            state.customFilter.bookingListFilter = '';
            state.customFilter.showDelay = true;
            state.customFilter.showCancel = true;

            switch (action.payload) {
                case 'Collections_and_Injections':
                    state.customFilter.jobType = ['Acceptance', 'Inject'];
                    state.customFilter.bookingType = 'ABC';
                    break;
                case 'Acceptance':
                    state.customFilter.jobType = ['Acceptance'];
                    state.customFilter.bookingType = 'ABC';
                    break;
                case 'Inject':
                    state.customFilter.jobType = ['Inject'];
                    state.customFilter.bookingType = 'ABC';
                    state.customFilter.showDelay = false;
                    state.customFilter.showCancel = false;
                    break;
                case 'Collection_and_Delivery':
                    state.customFilter.jobType = ['Collection', 'Delivery'];
                    state.customFilter.bookingType = 'CCD';
                    break;
                case 'Collection':
                    state.customFilter.jobType = ['Collection'];
                    state.customFilter.bookingType = 'CCD';
                    state.customFilter.showDelay = false;
                    break;
                case 'Delivery':
                    state.customFilter.jobType = ['Delivery'];
                    state.customFilter.bookingType = 'CCD';
                    state.customFilter.showCancel = false;
                    break;
                default:
                    state.customFilter.jobType = [];
                    state.customFilter.bookingType = 'ALL';
                    break;
            }
        },
        setBookingStatus: (state, action: PayloadAction<ISelectedJobStatus>) => {
            state.customFilter.selectedJobStatus = action.payload;
            state.commonFilter = '';
            state.hasfilterApplied = true;
        },
        setBookingPlanType: (state, action: PayloadAction<IPlanType>) => {
            state.customFilter.planType = action.payload;
            state.commonFilter = '';
            state.hasfilterApplied = true;
        },
        setNotes: (state, action: PayloadAction<IActivityFeed[]>) => {
            state.activityFeed = action.payload;
        },
        addNote: (state, action: PayloadAction<IActivityFeed>) => {
            state.activityFeed = [action.payload, ...state.activityFeed];
        },
        setExternalAgentChat: (state, action: PayloadAction<IAgentChat[]>) => {
            state.externalAgentChat = action.payload;
        },
        addExternalAgentChat: (state, action: PayloadAction<IAgentChat>) => {
            state.externalAgentChat = [action.payload, ...state.externalAgentChat];
        },
        updateAirportFilter: (state, action: PayloadAction<string>) => {
            state.filterByAirport = action.payload;
            state.hasfilterApplied = true;
            state.commonFilter = '';
        },
        updateAirlineFilter: (state, action: PayloadAction<any>) => {
            state.filterByAirline = action.payload;
            state.hasfilterApplied = true;
            state.commonFilter = '';
        },
        setOperatingAirline: (state, action: PayloadAction<any>) => {
            state.customFilter.operatingAirlineCode = action.payload.operatingAirlineCode;
            state.customFilter.operatingFlightNumber = action.payload.operatingFlightNumber;
            state.customFilter.operatingFlightDateTime = action.payload.operatingFlightDateTime;
        },
        clearOperatingAirline: (state) => {
            state.customFilter.operatingAirlineCode = '';
            state.customFilter.operatingFlightNumber = 0;
            state.customFilter.operatingFlightDateTime = '';
        },
        setBookingJourneyDetails: (state, action: PayloadAction<IBooking>) => {
            state.bookingDetails = action.payload;
        },
        setOrderByStatus: (state, action: PayloadAction<any>) => {
            state.orderBy = action.payload;
        },
    },
});

export const {
    setCommonFilter,
    setScheduledDate,
    setBookingtype,
    setBookingStatus,
    setBookingListFilter,
    setBookingAgent,
    setDefaultBookingAgent,
    clearBookingFilters,
    applyBookingFilter,
    applyJobFilter,
    clearAllFilter,
    setNotes,
    addNote,
    updateAirportFilter,
    updateAirlineFilter,
    setOperatingAirline,
    clearOperatingAirline,
    setBookingJourneyDetails,
    setOrderByStatus,
    setHubdropBookingFilter,
    setBookingPlanType,
    setExternalAgentChat,
    addExternalAgentChat
} = bookingSlice.actions;
export default bookingSlice.reducer;
